const hasText = (value) => typeof value === 'string' && value.replace(/<[^>]*>/g, ' ').trim().length > 0;

const hasEntries = (value) => Array.isArray(value) && value.some((entry) => typeof entry === 'string' && entry.trim());

const normalizeEntries = (value) =>
    Array.isArray(value)
        ? value
              .filter((entry) => typeof entry === 'string')
              .map((entry) => entry.trim())
              .filter(Boolean)
        : [];

const hasUnknownCriterionValues = (criterion) =>
    Object.entries(criterion).some(([key, value]) => {
        if (['grading_instructions', 'look_fors', 'violations_omissions'].includes(key)) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') return Object.keys(value).length > 0;
        return value !== null && value !== undefined && value !== '';
    });

export const hasCriterionInstructions = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    return hasText(value.grading_instructions) || hasEntries(value.look_fors) || hasEntries(value.violations_omissions);
};

export const addCriterionListInstruction = (criterion, listKey, value) => {
    const instruction = value.trim();
    if (!instruction) return criterion;
    return {
        ...criterion,
        [listKey]: [...(criterion[listKey] || []), instruction],
    };
};

export const removeCriterionListInstruction = (criterion, listKey, index) => ({
    ...criterion,
    [listKey]: (criterion[listKey] || []).filter((_, currentIndex) => currentIndex !== index),
});

/**
 * Keeps additive criterion settings in their persisted shape while excluding criteria with no
 * instructions. Unknown fields count as data and are retained for future settings versions.
 */
export const normalizeGradingSettings = (criteria) => {
    if (!criteria || typeof criteria !== 'object' || Array.isArray(criteria)) return null;

    const normalizedCriteria = Object.entries(criteria).reduce((result, [criterionId, value]) => {
        const criterion = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
        if (!hasCriterionInstructions(criterion) && !hasUnknownCriterionValues(criterion)) return result;

        result[criterionId] = {
            ...criterion,
            grading_instructions: criterion.grading_instructions ?? '',
            look_fors: normalizeEntries(criterion.look_fors),
            violations_omissions: normalizeEntries(criterion.violations_omissions),
        };
        return result;
    }, {});

    return Object.keys(normalizedCriteria).length ? { criteria: normalizedCriteria } : null;
};

export const mergeGradingSettings = (currentSettings, criteria) => {
    const unknownSettings = {
        ...(currentSettings && typeof currentSettings === 'object' && !Array.isArray(currentSettings)
            ? currentSettings
            : {}),
    };
    delete unknownSettings.criteria;
    const normalizedSettings = normalizeGradingSettings(criteria);
    if (normalizedSettings) return { ...unknownSettings, ...normalizedSettings };
    return Object.keys(unknownSettings).length ? unknownSettings : null;
};
